import React, { useState } from 'react';
import { useDeals } from '../context/DealsContext';
import { useQuota } from '../context/QuotaContext';
import StageDistribution from '../components/StageDistribution';
import QualityTrends from '../components/QualityTrends';
import ValueAnalysis from '../components/ValueAnalysis';
import QuotaProgress from '../components/QuotaProgress';
import { FiDownload, FiCheckSquare, FiTarget, FiCalendar } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const Analytics = () => {
  const { deals } = useDeals();
  const { quota, getAdjustedQuota } = useQuota();
  const [timeframe, setTimeframe] = useState('all');
  const [quotaPeriod, setQuotaPeriod] = useState(quota.period);

  // Filter deals by timeframe
  const filteredDeals = timeframe === 'all'
    ? deals
    : deals.filter(deal => {
        const activityDate = new Date(deal.lastActivity);
        const now = new Date();
        const daysDiff = Math.floor((now - activityDate) / (1000 * 60 * 60 * 24));
        if (timeframe === 'week') return daysDiff <= 7;
        if (timeframe === 'month') return daysDiff <= 30;
        if (timeframe === 'quarter') return daysDiff <= 90;
        return true;
      });

  const getAnalytics = () => {
    const stageDistribution = filteredDeals.reduce((acc, deal) => {
      acc[deal.stage] = (acc[deal.stage] || 0) + 1;
      return acc;
    }, {});

    const qualityDistribution = {
      high: filteredDeals.filter(d => d.qualityScore >= 8).length,
      medium: filteredDeals.filter(d => d.qualityScore >= 6 && d.qualityScore < 8).length,
      low: filteredDeals.filter(d => d.qualityScore < 6).length
    };

    const avgValueByStage = filteredDeals.reduce((acc, deal) => {
      if (!acc[deal.stage]) {
        acc[deal.stage] = { total: 0, count: 0 };
      }
      acc[deal.stage].total += deal.value;
      acc[deal.stage].count += 1;
      return acc;
    }, {});

    Object.keys(avgValueByStage).forEach(stage => {
      avgValueByStage[stage] = avgValueByStage[stage].total / avgValueByStage[stage].count;
    });

    // Deal strength analysis
    const dealStrength = {
      strong: filteredDeals.filter(
        d => d.purchaserName && d.purchaserContact && d.projectedCloseDate
      ).length,
      withPrimaryPurchaser: filteredDeals.filter(
        d => d.purchaserName && d.purchaserContact
      ).length,
      withSecondaryPurchaser: filteredDeals.filter(
        d => d.purchaserName2 && d.purchaserContact2
      ).length,
      withCloseDate: filteredDeals.filter(
        d => d.projectedCloseDate
      ).length,
      total: filteredDeals.length
    };

    // Quota-related metrics
    const currentQuota = getAdjustedQuota(quotaPeriod);
    const totalPipelineValue = filteredDeals.reduce((sum, deal) => sum + deal.value, 0);
    const weightedPipelineValue = filteredDeals.reduce(
      (sum, deal) => sum + (deal.value * (deal.probability / 100)),
      0
    );
    const quotaAttainment = (weightedPipelineValue / currentQuota) * 100;

    return {
      stageDistribution,
      qualityDistribution,
      avgValueByStage,
      dealStrength,
      quotaAttainment: Math.min(quotaAttainment, 100),
      totalPipelineValue,
      weightedPipelineValue,
      currentQuota
    };
  };

  const analytics = getAnalytics();

  // Export data as CSV
  const exportCSV = () => {
    const headers = [
      'Company',
      'Contact',
      'Value',
      'Stage',
      'Quality Score',
      'Probability',
      'Last Activity',
      'Primary Purchaser Name',
      'Primary Purchaser Contact',
      'Secondary Purchaser Name',
      'Secondary Purchaser Contact',
      'Projected Close Date'
    ];

    const csvRows = [headers];
    filteredDeals.forEach(deal => {
      const row = [
        deal.company,
        deal.contact,
        deal.value,
        deal.stage,
        deal.qualityScore,
        deal.probability,
        deal.lastActivity,
        deal.purchaserName || '',
        deal.purchaserContact || '',
        deal.purchaserName2 || '',
        deal.purchaserContact2 || '',
        deal.projectedCloseDate || ''
      ];
      csvRows.push(row);
    });

    const csvContent = csvRows.map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.setAttribute('href', url);
    a.setAttribute('download', `deal-analytics-${new Date().toISOString().split('T')[0]}.csv`);
    a.click();
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Analytics</h1>
        <div className="flex flex-wrap items-center gap-3">
          <div className="text-sm text-gray-500 dark:text-gray-400">
            Based on {filteredDeals.length} deals
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {['all', 'week', 'month', 'quarter'].map(option => (
              <button
                key={option}
                onClick={() => setTimeframe(option)}
                className={`px-3 py-1 text-xs rounded-md transition-colors ${
                  timeframe === option
                    ? 'bg-blue-600 text-white'
                    : 'bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                }`}
              >
                {option === 'all'
                  ? 'All Time'
                  : option === 'week'
                  ? 'Last Week'
                  : option === 'month'
                  ? 'Last Month'
                  : 'Last Quarter'}
              </button>
            ))}
            <button
              onClick={exportCSV}
              className="p-1 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded transition-colors"
              title="Export data"
            >
              <SafeIcon icon={FiDownload} className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Quota Period Selection */}
      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
          <div className="flex items-center space-x-2">
            <SafeIcon icon={FiTarget} className="w-5 h-5 text-green-600 dark:text-green-500" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Quota Analysis</h3>
          </div>
          <div className="flex items-center space-x-2">
            <SafeIcon icon={FiCalendar} className="w-4 h-4 text-gray-500 dark:text-gray-400" />
            <span className="text-sm text-gray-600 dark:text-gray-400">View by:</span>
            <div className="flex border border-gray-300 dark:border-gray-600 rounded-md overflow-hidden">
              {['monthly', 'quarterly', 'yearly'].map(period => (
                <button
                  key={period}
                  onClick={() => setQuotaPeriod(period)}
                  className={`px-3 py-1 text-xs font-medium capitalize ${
                    quotaPeriod === period
                      ? 'bg-blue-600 text-white'
                      : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  {period}
                </button>
              ))}
            </div>
          </div>
        </div>
        <QuotaProgress periodOverride={quotaPeriod} />
      </div>

      {/* Deal Strength Analysis */}
      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="flex items-center space-x-2 mb-4">
          <SafeIcon icon={FiCheckSquare} className="w-5 h-5 text-blue-600 dark:text-blue-500" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Deal Strength Analysis</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-100 dark:border-blue-800/40">
            <div className="text-sm font-medium text-blue-700 dark:text-blue-400 mb-1">Strong Deals</div>
            <div className="text-2xl font-bold text-blue-900 dark:text-blue-300">{analytics.dealStrength.strong}</div>
            <div className="text-xs text-blue-600 dark:text-blue-500 mt-1">
              {analytics.dealStrength.total > 0
                ? `${Math.round((analytics.dealStrength.strong / analytics.dealStrength.total) * 100)}%`
                : '0%'} of total
            </div>
            <div className="w-full bg-blue-200 dark:bg-blue-800/40 rounded-full h-2 mt-2">
              <div
                className="bg-blue-600 dark:bg-blue-500 h-2 rounded-full"
                style={{
                  width: `${
                    analytics.dealStrength.total > 0
                      ? (analytics.dealStrength.strong / analytics.dealStrength.total) * 100
                      : 0
                  }%`
                }}
              />
            </div>
          </div>
          <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg border border-green-100 dark:border-green-800/40">
            <div className="text-sm font-medium text-green-700 dark:text-green-400 mb-1">With Primary Purchaser</div>
            <div className="text-2xl font-bold text-green-900 dark:text-green-300">
              {analytics.dealStrength.withPrimaryPurchaser}
            </div>
            <div className="text-xs text-green-600 dark:text-green-500 mt-1">
              {analytics.dealStrength.total > 0
                ? `${Math.round(
                    (analytics.dealStrength.withPrimaryPurchaser / analytics.dealStrength.total) * 100
                  )}%`
                : '0%'} of total
            </div>
            <div className="w-full bg-green-200 dark:bg-green-800/40 rounded-full h-2 mt-2">
              <div
                className="bg-green-600 dark:bg-green-500 h-2 rounded-full"
                style={{
                  width: `${
                    analytics.dealStrength.total > 0
                      ? (analytics.dealStrength.withPrimaryPurchaser / analytics.dealStrength.total) * 100
                      : 0
                  }%`
                }}
              />
            </div>
          </div>
          <div className="bg-orange-50 dark:bg-orange-900/20 p-4 rounded-lg border border-orange-100 dark:border-orange-800/40">
            <div className="text-sm font-medium text-orange-700 dark:text-orange-400 mb-1">With Secondary Purchaser</div>
            <div className="text-2xl font-bold text-orange-900 dark:text-orange-300">
              {analytics.dealStrength.withSecondaryPurchaser}
            </div>
            <div className="text-xs text-orange-600 dark:text-orange-500 mt-1">
              {analytics.dealStrength.total > 0
                ? `${Math.round(
                    (analytics.dealStrength.withSecondaryPurchaser / analytics.dealStrength.total) * 100
                  )}%`
                : '0%'} of total
            </div>
            <div className="w-full bg-orange-200 dark:bg-orange-800/40 rounded-full h-2 mt-2">
              <div
                className="bg-orange-600 dark:bg-orange-500 h-2 rounded-full"
                style={{
                  width: `${
                    analytics.dealStrength.total > 0
                      ? (analytics.dealStrength.withSecondaryPurchaser / analytics.dealStrength.total) * 100
                      : 0
                  }%`
                }}
              />
            </div>
          </div>
          <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg border border-purple-100 dark:border-purple-800/40">
            <div className="text-sm font-medium text-purple-700 dark:text-purple-400 mb-1">With Close Date</div>
            <div className="text-2xl font-bold text-purple-900 dark:text-purple-300">{analytics.dealStrength.withCloseDate}</div>
            <div className="text-xs text-purple-600 dark:text-purple-500 mt-1">
              {analytics.dealStrength.total > 0
                ? `${Math.round((analytics.dealStrength.withCloseDate / analytics.dealStrength.total) * 100)}%`
                : '0%'} of total
            </div>
            <div className="w-full bg-purple-200 dark:bg-purple-800/40 rounded-full h-2 mt-2">
              <div
                className="bg-purple-600 dark:bg-purple-500 h-2 rounded-full"
                style={{
                  width: `${
                    analytics.dealStrength.total > 0
                      ? (analytics.dealStrength.withCloseDate / analytics.dealStrength.total) * 100
                      : 0
                  }%`
                }}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Quality Distribution</h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-400">High Quality (8+)</span>
              <div className="flex items-center space-x-2">
                <div className="w-20 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                  <div
                    className="bg-green-500 dark:bg-green-500 h-2 rounded-full"
                    style={{
                      width: `${
                        filteredDeals.length
                          ? (analytics.qualityDistribution.high / filteredDeals.length) * 100
                          : 0
                      }%`
                    }}
                  />
                </div>
                <span className="text-sm font-medium text-gray-900 dark:text-gray-300">
                  {analytics.qualityDistribution.high}
                </span>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-400">Medium Quality (6-8)</span>
              <div className="flex items-center space-x-2">
                <div className="w-20 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                  <div
                    className="bg-yellow-500 dark:bg-yellow-500 h-2 rounded-full"
                    style={{
                      width: `${
                        filteredDeals.length
                          ? (analytics.qualityDistribution.medium / filteredDeals.length) * 100
                          : 0
                      }%`
                    }}
                  />
                </div>
                <span className="text-sm font-medium text-gray-900 dark:text-gray-300">
                  {analytics.qualityDistribution.medium}
                </span>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-400">Low Quality (&lt;6)</span>
              <div className="flex items-center space-x-2">
                <div className="w-20 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                  <div
                    className="bg-red-500 dark:bg-red-500 h-2 rounded-full"
                    style={{
                      width: `${
                        filteredDeals.length
                          ? (analytics.qualityDistribution.low / filteredDeals.length) * 100
                          : 0
                      }%`
                    }}
                  />
                </div>
                <span className="text-sm font-medium text-gray-900 dark:text-gray-300">
                  {analytics.qualityDistribution.low}
                </span>
              </div>
            </div>
          </div>
        </div>
        <StageDistribution data={analytics.stageDistribution} />
        <ValueAnalysis data={analytics.avgValueByStage} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <QualityTrends />
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Key Insights</h3>
          <div className="space-y-4">
            <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800/40">
              <h4 className="font-medium text-black dark:text-white">Deal Strength</h4>
              <p className="text-sm text-black dark:text-white mt-1">
                {analytics.dealStrength.strong > 0
                  ? `${analytics.dealStrength.strong} deals (${Math.round(
                      (analytics.dealStrength.strong / analytics.dealStrength.total) * 100
                    )}%) have all strength factors`
                  : 'No deals have all strength factors'}
              </p>
              <p className="text-xs text-black dark:text-white mt-2">
                Adding purchaser contact information and close date improves deal quality
              </p>
            </div>

            <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800/40">
              <h4 className="font-medium text-black dark:text-white">Improvement Opportunity</h4>
              <p className="text-sm text-black dark:text-white mt-1">
                {analytics.dealStrength.total - analytics.dealStrength.strong > 0
                  ? `${
                      analytics.dealStrength.total - analytics.dealStrength.strong
                    } deals need additional strength factors`
                  : 'All deals have complete strength factors'}
              </p>
              {analytics.dealStrength.total > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {analytics.dealStrength.withPrimaryPurchaser < analytics.dealStrength.total && (
                    <span className="text-xs bg-red-100 dark:bg-red-900/30 text-black dark:text-white px-2 py-1 rounded">
                      {analytics.dealStrength.total - analytics.dealStrength.withPrimaryPurchaser} missing primary purchaser
                    </span>
                  )}
                  {analytics.dealStrength.withSecondaryPurchaser < analytics.dealStrength.total && (
                    <span className="text-xs bg-orange-100 dark:bg-orange-900/30 text-black dark:text-white px-2 py-1 rounded">
                      {analytics.dealStrength.total - analytics.dealStrength.withSecondaryPurchaser} missing secondary purchaser
                    </span>
                  )}
                  {analytics.dealStrength.withCloseDate < analytics.dealStrength.total && (
                    <span className="text-xs bg-red-100 dark:bg-red-900/30 text-black dark:text-white px-2 py-1 rounded">
                      {analytics.dealStrength.total - analytics.dealStrength.withCloseDate} missing close date
                    </span>
                  )}
                </div>
              )}
            </div>

            <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-800/40">
              <h4 className="font-medium text-black dark:text-white">Pipeline vs Quota</h4>
              <div className="flex items-center justify-between mt-2">
                <span className="text-sm text-black dark:text-white">Quota Attainment:</span>
                <span className="text-sm font-medium text-black dark:text-white">
                  {Math.round(analytics.quotaAttainment)}%
                </span>
              </div>
              <div className="w-full bg-purple-200 dark:bg-purple-800/40 rounded-full h-2 mt-1 mb-2">
                <div
                  className="bg-purple-600 dark:bg-purple-500 h-2 rounded-full"
                  style={{ width: `${analytics.quotaAttainment}%` }}
                />
              </div>
              <p className="text-xs text-black dark:text-white mt-1">
                Weighted pipeline value: ${analytics.weightedPipelineValue.toLocaleString()}
              </p>
              <p className="text-xs text-black dark:text-white">
                Current {quotaPeriod} quota: ${analytics.currentQuota.toLocaleString()}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Analytics;